import { readMoney } from "@pharmacy/domain";
import { Eye, EyeOff, Search, X } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { useT } from "@/i18n/provider";
import { cx } from "./cx";
import { Spinner } from "./spinner";

/**
 * Fields (the phone's Field and PhoneField, apps/mobile/src/components/ui).
 *
 * The label above (14 px, 600), the box 48 px high, a hint or an error under
 * it. On focus both the border and the ground change, as on the phone: on a
 * form of identical boxes, "which one am I typing in" is the first question.
 * An error turns the border red and is announced (role="alert").
 *
 * Works with React Hook Form's register(): every other prop, the ref
 * included, goes to the <input>.
 */

export type FieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
  label: string;
  /** Under the box, muted. Not shown while there is an error. */
  hint?: string;
  /** Present means the field is wrong. */
  error?: string;
  /** Inside the box, before the text: "$", the country code. */
  prefix?: React.ReactNode;
  /** Inside the box, after the text: a show-password button. */
  suffix?: React.ReactNode;
  /** Under the box, on the right: a "Forgot password?" link. */
  action?: React.ReactNode;
  ref?: React.Ref<HTMLInputElement>;
};

export function Field({ label, hint, error, prefix, suffix, action, className, id, ...input }: FieldProps) {
  const ownId = useId();
  const inputId = id ?? ownId;
  const noteId = `${inputId}-note`;
  const note = error ?? hint;

  return (
    <div className={cx("flex flex-col gap-1", className)}>
      <label htmlFor={inputId} className="text-label font-semibold text-text">
        {label}
      </label>
      <div
        className={cx(
          "flex min-h-12 items-stretch overflow-hidden rounded-control border-[1.5px] bg-surface transition-colors",
          "focus-within:border-primary focus-within:bg-primary-soft",
          error ? "border-danger" : "border-input-border",
          input.disabled && "bg-disabled-surface",
        )}
      >
        {prefix}
        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={note ? noteId : undefined}
          // Off, as on the phone: these are usernames and shop names, and a
          // browser "helpfully" correcting a username is a support call.
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="min-w-0 flex-1 bg-transparent px-3 text-input text-text outline-none placeholder:text-muted disabled:cursor-not-allowed"
          {...input}
        />
        {suffix}
      </div>
      {error ? (
        <p id={noteId} role="alert" className="text-small font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={noteId} className="text-small text-muted">
          {hint}
        </p>
      ) : null}
      {action && <div className="mt-1 flex justify-end">{action}</div>}
    </div>
  );
}

/** A password, hidden until the eye is pressed. */
export function PasswordField(props: Omit<FieldProps, "type" | "suffix">) {
  const t = useT();
  const [shown, setShown] = useState(false);
  return (
    <Field
      {...props}
      type={shown ? "text" : "password"}
      suffix={
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? t.hidePassword : t.showPassword}
          aria-pressed={shown}
          className="flex w-12 items-center justify-center text-muted hover:text-primary"
        >
          {shown ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
        </button>
      }
    />
  );
}

/**
 * A Somali phone number with +252 fixed in front. The person types the nine
 * digits after it; a leading 0 (the trunk prefix, never part of the number) is
 * dropped as they type, so "0615550100" and "615550100" end the same. The
 * value is those nine digits; normalizePhone() makes the +252… form to send.
 */
export function PhoneField({
  value,
  onChange,
  ...rest
}: Omit<FieldProps, "value" | "onChange" | "prefix" | "type"> & {
  value: string;
  onChange: (digits: string) => void;
}) {
  return (
    <Field
      {...rest}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      value={value}
      onChange={(e) => onChange(nineDigits(e.target.value))}
      prefix={
        <span className="flex items-center gap-1 border-r border-border pr-2 pl-3 text-input font-semibold text-body">
          {/* A picture, not the 🇸🇴 emoji: Windows draws flag emoji as the letters "SO". */}
          <img src="/flag-so.svg" alt="" width={21} height={14} className="rounded-[2px]" />
          <span>+252</span>
        </span>
      }
    />
  );
}

/**
 * The nine digits after +252, from whatever was typed or pasted:
 * "0615550100", "615550100", "+252 61 555 0100" and "252615550100" all give
 * "615550100". No maxLength on the box: the browser would cut a pasted
 * "0615550100" to nine characters before the 0 is dropped, losing a digit.
 */
export function nineDigits(input: string): string {
  let digits = input.replace(/\D/g, "");
  if (digits.length > 9 && digits.startsWith("252")) digits = digits.slice(3);
  return digits.replace(/^0+/, "").slice(0, 9);
}

/**
 * Money, with $ in front and the number keypad on phones. The value is the
 * text as typed; read it with readMoney() from @pharmacy/domain. Text that is
 * not an amount (or has more than 10 decimals) says so when the person leaves
 * the box, unless the form gives its own error.
 */
export function MoneyField({ error, onBlur, ...rest }: Omit<FieldProps, "prefix" | "type" | "inputMode">) {
  const t = useT();
  const [unreadable, setUnreadable] = useState(false);

  return (
    <Field
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      error={error ?? (unreadable ? t.notAnAmount : undefined)}
      onBlur={(e) => {
        const text = e.target.value.trim();
        setUnreadable(text !== "" && readMoney(text) === null);
        onBlur?.(e);
      }}
      onInput={() => setUnreadable(false)}
      prefix={<span className="flex items-center pl-3 text-input font-semibold text-muted">$</span>}
    />
  );
}

/**
 * A search box: a magnifier, a clear ×, and a small spinner while the
 * results come. Pair it with useDebouncedValue so a search is asked 250 ms
 * after the typing stops, not on every key.
 */
export function SearchBox({
  value,
  onChange,
  placeholder,
  busy,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Also its accessible name: say what can be searched. */
  placeholder: string;
  busy?: boolean;
  className?: string;
  autoFocus?: boolean;
}) {
  const t = useT();
  return (
    <div
      className={cx(
        "flex h-12 w-full max-w-[600px] items-center gap-2 rounded-control border-[1.5px] border-input-border bg-surface px-3",
        "transition-colors focus-within:border-primary focus-within:bg-primary-soft",
        className,
      )}
    >
      <Search size={20} aria-hidden="true" className="shrink-0 text-muted" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && value) {
            e.stopPropagation();
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        className="min-w-0 flex-1 bg-transparent text-input text-text outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden"
      />
      {busy && <Spinner size={16} className="text-primary" />}
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={t.clearSearch}
          className="-mr-1 flex size-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-primary-soft hover:text-primary"
        >
          <X size={18} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** `value`, but only once it has stopped changing for `ms`: the search to ask the server. */
export function useDebouncedValue<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}
